import { useEffect, useMemo, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { supabase } from '../services/supabaseClient';

const ACTIVE_FARM_STORAGE_KEY = 'activeFarmId';

export type UserFarmOption = {
  id: string;
  name: string;
  location: string | null;
  size: number | null;
  created_at: string | null;
};

export const useUserFarmSelection = () => {
  const { user } = useAuth();
  const [farms, setFarms] = useState<UserFarmOption[]>([]);
  const [selectedFarmId, setSelectedFarmIdState] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    const loadFarms = async () => {
      if (!user?.id) {
        if (!cancelled) {
          setFarms([]);
          setSelectedFarmIdState('');
          setError(null);
          setLoading(false);
        }
        return;
      }

      setLoading(true);
      setError(null);

      try {
        const { data, error: farmsError } = await supabase
          .from('farms')
          .select('id, name, location, size, created_at')
          .eq('user_id', user.id)
          .order('created_at', { ascending: false });

        if (farmsError) {
          throw farmsError;
        }

        if (cancelled) return;

        const nextFarms = ((data as UserFarmOption[] | null) ?? []).map((farm) => ({
          id: String(farm.id),
          name: String(farm.name ?? 'Untitled farm'),
          location: farm.location ? String(farm.location) : null,
          size: typeof farm.size === 'number' ? farm.size : farm.size ? Number(farm.size) : null,
          created_at: farm.created_at ? String(farm.created_at) : null,
        }));

        setFarms(nextFarms);

        const storedFarmId = localStorage.getItem(ACTIVE_FARM_STORAGE_KEY);
        const fallbackFarmId = nextFarms[0]?.id ?? '';
        const resolvedFarmId =
          storedFarmId && nextFarms.some((farm) => farm.id === storedFarmId)
            ? storedFarmId
            : fallbackFarmId;

        setSelectedFarmIdState(resolvedFarmId);

        if (resolvedFarmId) {
          localStorage.setItem(ACTIVE_FARM_STORAGE_KEY, resolvedFarmId);
        } else {
          localStorage.removeItem(ACTIVE_FARM_STORAGE_KEY);
        }
      } catch (loadError: any) {
        if (cancelled) return;
        setFarms([]);
        setSelectedFarmIdState('');
        setError(loadError?.message || 'Failed to load your farms.');
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    void loadFarms();

    return () => {
      cancelled = true;
    };
  }, [user?.id]);

  const setSelectedFarmId = (farmId: string) => {
    setSelectedFarmIdState(farmId);
    if (farmId) {
      localStorage.setItem(ACTIVE_FARM_STORAGE_KEY, farmId);
    } else {
      localStorage.removeItem(ACTIVE_FARM_STORAGE_KEY);
    }
  };

  const selectedFarm = useMemo(
    () => farms.find((farm) => farm.id === selectedFarmId) ?? null,
    [farms, selectedFarmId],
  );

  return {
    farms,
    selectedFarm,
    selectedFarmId,
    setSelectedFarmId,
    loading,
    error,
  };
};
